import { useMemo, useState } from "react";
import { money } from "@/lib/format";
import { TaxOverrideInline } from "@/components/common/TaxOverrideDialog";
import { MarginsCard } from "./MarginsCard";
import { TaxFeesCard } from "./TaxFeesCard";
import { TotalsCard, CollapseButton } from "./TotalsCard";
import { OtherCostsCard } from "./OtherCostsCard";
import { grossMargin, railWarnings, marginLines } from "./railMath";

const KEY = "abs.proposals.railCollapsed";
const Stat = ({ label, children, testId }) => (
  <span className="inline-flex items-baseline gap-1 whitespace-nowrap text-xs text-slate-500">{label}<span className="tabular-nums font-semibold text-slate-900" data-testid={testId}>{children}</span></span>
);

function RailStrip({ totals, form, gm, onToggle }) {
  const rate = form.tax_override?.type === "rate" ? form.tax_override.rate : form.tax_rate;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5" data-testid="rail-strip">
      <Stat label="Material">{money(totals.totalMaterial)}</Stat>
      <Stat label="Labor">{money(totals.totalLabor)}</Stat>
      <Stat label="Freight">{money(totals.totalFreight)}</Stat>
      <Stat label="Subtotal">{money(totals.subtotal)}</Stat>
      {totals.discountAmount > 0 && <Stat label="Discount">−{money(totals.discountAmount)}</Stat>}
      <Stat label={`Tax ${parseFloat(rate) || 0}%`}>{money(totals.taxAmount)}</Stat>
      <span className="ml-auto inline-flex items-baseline gap-1.5 whitespace-nowrap text-xs font-bold text-slate-900">TOTAL SALES<span className="text-sm tabular-nums" data-testid="rail-strip-total">{money(totals.total)}</span></span>
      <Stat label="GM" testId="rail-strip-gm">{gm.pct == null ? "—" : `${gm.pct.toFixed(1)}%`}</Stat>
      <CollapseButton collapsed onToggle={onToggle} />
    </div>
  );
}

// Global controls for the whole proposal. Sits below the grid; on wide screens it pins to the bottom while the grid scrolls.
export function ProposalRail({ form, setForm, totals, proposalId, canOverrideTax, taxOverrideOpen, setTaxOverrideOpen, applyTaxOverrideResult, onHideTax, masterParts, reviewable, onApplyMargins, updateScope, onAutoDrive }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(KEY) === "1");
  const toggle = () => setCollapsed((c) => { localStorage.setItem(KEY, c ? "0" : "1"); return !c; });
  const gm = useMemo(() => grossMargin(totals), [totals]);
  const warnings = useMemo(() => railWarnings(form.scopes, masterParts, reviewable), [form.scopes, masterParts, reviewable]);
  const shared = { totals, form, gm, onToggle: toggle };
  return (
    <div className="2xl:sticky 2xl:bottom-0 2xl:z-20 2xl:-mx-2 2xl:bg-background/95 2xl:px-2 2xl:py-2 2xl:backdrop-blur" data-testid="proposal-rail" data-collapsed={collapsed ? "1" : "0"}>
      {collapsed ? <RailStrip {...shared} /> : (
        <div className="grid gap-3 md:grid-cols-4">
          <MarginsCard defaults={form.default_margins} onChange={(d) => setForm((f) => ({ ...f, default_margins: d }))} onApplyAll={onApplyMargins} lineCount={marginLines(form.scopes)} />
          <TaxFeesCard form={form} setForm={setForm} totals={totals} proposalId={proposalId} canOverrideTax={canOverrideTax}
            onOverride={() => setTaxOverrideOpen(true)} applyTaxOverrideResult={applyTaxOverrideResult} onHideTax={onHideTax} />
          <OtherCostsCard scopes={form.scopes} updateScope={updateScope} onAutoDrive={onAutoDrive} />
          <TotalsCard {...shared} warnings={warnings} collapsed={false} />
        </div>
      )}
      {proposalId && (
        <div className={taxOverrideOpen ? "mt-3" : ""}>
          <TaxOverrideInline open={taxOverrideOpen} onOpenChange={setTaxOverrideOpen} docType="proposal" docId={proposalId}
            current={{ tax_rate: form.tax_override?.type === "rate" ? form.tax_override.prior_rate : form.tax_rate, tax_authority: form.tax_authority, taxable_base: totals.taxableBase, tax_amount: totals.taxAmount, tax_override: form.tax_override }}
            onApplied={applyTaxOverrideResult} />
        </div>
      )}
    </div>
  );
}
